import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { REPORT_TYPES } from "../lib/constants";
import { FileText, Printer, ShieldCheck, Dna, Sun, Recycle, Users } from "lucide-react";
import { PermissionStatusBadge, ClimateRiskBadge, VisibilityBadge } from "../components/Badges";

const ICONS = {
  provenance: FileText,
  cultural: ShieldCheck,
  climate: Sun,
  genetic: Dna,
  restoration: Recycle,
  partner: Users,
};

export default function Reports() {
  const [type, setType] = useState("provenance");
  const [cid, setCid] = useState("");

  const { data: collections = [] } = useQuery({
    queryKey: ["collections", "for-reports"],
    queryFn: async () => (await api.get("/collections")).data,
  });

  const { data: report } = useQuery({
    queryKey: ["report", type, cid],
    enabled: !!cid && !!type,
    queryFn: async () => (await api.get(`/reports/${type}/${cid}`)).data,
  });

  return (
    <div className="space-y-8" data-testid="reports-page">
      <header>
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B]">Reports</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-[#2C2A28] mt-2">Generate provenance reports</h1>
        <p className="text-[13.5px] text-[#5C5854] mt-2">Printable summaries for partners, governance and project teams.</p>
      </header>

      <Card className="p-7 border-[#E6E2D8] shadow-sm bg-white no-print">
        <div className="grid md:grid-cols-[1fr_2fr_auto] gap-4 items-end">
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854] mb-2">Report type</div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="bg-white border-[#E6E2D8]" data-testid="report-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REPORT_TYPES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#5C5854] mb-2">Collection</div>
            <Select value={cid || undefined} onValueChange={setCid}>
              <SelectTrigger className="bg-white border-[#E6E2D8]" data-testid="report-collection">
                <SelectValue placeholder="Choose a collection" />
              </SelectTrigger>
              <SelectContent>
                {collections.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.collection_id} · {c.species_scientific_name || "—"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => window.print()} disabled={!report} className="bg-[#8B5A2B] hover:bg-[#6B4420] text-[#FDFBF7] gap-2" data-testid="report-print">
            <Printer className="w-4 h-4" /> Print
          </Button>
        </div>

        <div className="mt-5 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {REPORT_TYPES.map((r) => {
            const Icon = ICONS[r.value] || FileText;
            return (
              <button
                key={r.value}
                onClick={() => setType(r.value)}
                className={`text-left rounded-md border px-3 py-3 transition-colors ${
                  type === r.value ? "border-[#8B5A2B] bg-[#FFF7E8]" : "border-[#E6E2D8] hover:bg-[#F4F1EA]"
                }`}
                data-testid={`report-quick-${r.value}`}
              >
                <Icon className="w-4 h-4 text-[#8B5A2B]" strokeWidth={1.6} />
                <div className="text-[12.5px] font-medium mt-1 leading-tight">{r.label}</div>
              </button>
            );
          })}
        </div>
      </Card>

      {!report && (
        <Card className="p-10 border-dashed border-[#E6E2D8] bg-transparent text-center text-[13px] text-[#5C5854]">
          Choose a collection to generate the {REPORT_TYPES.find((r) => r.value === type)?.label.toLowerCase()} report.
        </Card>
      )}

      {report && <ReportPaper report={report} type={type} />}
    </div>
  );
}

function ReportPaper({ report, type }) {
  const cul = report.cultural || {};
  const eco = report.ecological || {};
  const gen = report.genetic || {};
  const cli = report.climate || {};
  const res = report.restoration || {};
  const ai = report.ai_insights || {};

  return (
    <Card className="bg-white border-[#E6E2D8] shadow-sm p-10 print:shadow-none print:border-0" data-testid="report-paper">
      <header className="flex items-start justify-between border-b border-[#E6E2D8] pb-6">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B]">{REPORT_TYPES.find((r) => r.value === type)?.label} report</div>
          <h2 className="font-display text-3xl tracking-tight font-bold text-[#2C2A28] mt-1">SeedKeepers</h2>
          <p className="text-[12px] text-[#5C5854] mt-1 italic">Seed provenance intelligence for restoration, culture and climate resilience.</p>
        </div>
        <div className="text-right">
          <div className="font-mono-data text-[11px] text-[#8A857D]">{report.collection_id}</div>
          <div className="text-[11px] text-[#5C5854] mt-1">{new Date(report.generated_at).toLocaleDateString()}</div>
          <div className="text-[11px] text-[#5C5854]">Prepared for {report.generated_for?.name}</div>
        </div>
      </header>

      <section className="mt-6 grid md:grid-cols-3 gap-6">
        <Box label="Species">
          <div className="italic font-serif-display text-[18px] text-[#2C2A28]">{report.species_scientific_name || "—"}</div>
          <div className="text-[12.5px] text-[#5C5854]">{report.species_common_name}</div>
        </Box>
        <Box label="Collected">
          {report.date_collected || "—"} · {report.collector_name || "—"}
        </Box>
        <Box label="Project">
          {report.project_name || "—"} · {report.organisation || "—"}
        </Box>
      </section>

      <div className="flex flex-wrap items-center gap-2 mt-5">
        <PermissionStatusBadge status={cul.cultural_permission_status} />
        <ClimateRiskBadge score={cli.climate_risk_score} />
        <VisibilityBadge level={report.visibility_level} />
      </div>

      <Section title="Provenance summary">
        <p className="text-[13.5px] leading-relaxed text-[#2C2A28]">{ai.provenance_summary || "Not generated."}</p>
      </Section>

      {(type === "provenance" || type === "cultural" || type === "partner") && (
        <Section title="Cultural authority">
          <Grid>
            <KV k="TO group" v={cul.traditional_owner_group} />
            <KV k="Authority contact" v={cul.cultural_authority_contact} />
            <KV k="Permission status" v={cul.cultural_permission_status} />
            <KV k="Permission date" v={cul.permission_date} />
            <KV k="Public sharing" v={cul.public_sharing_allowed ? "Allowed" : "Not allowed"} />
            <KV k="Partner sharing" v={cul.partner_sharing_allowed ? "Allowed" : "Not allowed"} />
          </Grid>
        </Section>
      )}

      {(type === "provenance" || type === "climate" || type === "partner") && (
        <Section title="Climate exposure & resilience">
          <Grid>
            <KV k="Climate zone" v={cli.current_climate_zone} />
            <KV k="Mean rainfall" v={cli.mean_annual_rainfall ? `${cli.mean_annual_rainfall} mm` : null} />
            <KV k="Mean temperature" v={cli.mean_annual_temperature ? `${cli.mean_annual_temperature} °C` : null} />
            <KV k="Drought" v={cli.drought_exposure} />
            <KV k="Cyclone" v={cli.cyclone_exposure} />
            <KV k="Fire" v={cli.fire_exposure} />
            <KV k="Future suitability" v={cli.future_climate_suitability_rating} />
            <KV k="Risk score" v={cli.climate_risk_score} />
          </Grid>
          <p className="text-[13px] leading-relaxed text-[#2C2A28] mt-3">{ai.climate_summary}</p>
        </Section>
      )}

      {(type === "provenance" || type === "genetic" || type === "partner") && (
        <Section title="Genetic sampling">
          <Grid>
            <KV k="DNA sample" v={gen.dna_sample_taken ? "Taken" : "Not taken"} />
            <KV k="Sample ID" v={gen.dna_sample_id} />
            <KV k="Tissue type" v={gen.tissue_sample_type} />
            <KV k="Parents sampled" v={gen.parent_plants_sampled} />
            <KV k="Population size" v={gen.population_size_estimate} />
            <KV k="Diversity score" v={gen.genetic_diversity_score} />
            <KV k="Inbreeding risk" v={gen.inbreeding_risk} />
            <KV k="Hybridisation risk" v={gen.hybridisation_risk} />
          </Grid>
          <p className="text-[13px] leading-relaxed text-[#2C2A28] mt-3">{ai.genetic_summary}</p>
        </Section>
      )}

      {(type === "provenance" || type === "restoration" || type === "partner") && (
        <Section title="Restoration suitability">
          <Grid>
            <KV k="Recommended use" v={res.recommended_restoration_use} />
            <KV k="Project types" v={res.suitable_project_types} />
            <KV k="Carbon" v={res.carbon_project_suitability} />
            <KV k="Mine rehab" v={res.mine_rehabilitation_suitability} />
            <KV k="Biodiversity repair" v={res.biodiversity_repair_suitability} />
            <KV k="Viability" v={res.viability_percentage} />
            <KV k="Commercial use" v={res.commercial_use_permission} />
            <KV k="Benefit sharing" v={res.benefit_sharing_requirements} />
          </Grid>
          <p className="text-[13px] leading-relaxed text-[#2C2A28] mt-3">{ai.restoration_recommendations}</p>
        </Section>
      )}

      {(ai.risk_warnings?.length || ai.missing_data_alerts?.length) > 0 && (
        <Section title="Risks & gaps">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#B45B3E] mb-1.5">Risk warnings</div>
              <ul className="list-disc list-inside text-[12.5px] text-[#B45B3E] space-y-1">{(ai.risk_warnings || []).map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5854] mb-1.5">Missing data</div>
              <ul className="list-disc list-inside text-[12.5px] text-[#5C5854] space-y-1">{(ai.missing_data_alerts || []).map((m, i) => <li key={i}>{m}</li>)}</ul>
            </div>
          </div>
        </Section>
      )}

      <footer className="mt-10 pt-6 border-t border-[#E6E2D8] text-[11px] text-[#8A857D] leading-relaxed">
        This report is generated for internal use by SeedKeepers participants. Cultural information is shared subject to
        the permissions recorded against this collection. AI-generated content is labelled &ldquo;draft intelligence&rdquo; and must
        be verified by cultural and scientific authority before publication.
      </footer>
    </Card>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-7">
      <h3 className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B] mb-3">{title}</h3>
      {children}
    </section>
  );
}

function Box({ label, children }) {
  return (
    <div className="rounded-md border border-[#E6E2D8] p-4 bg-[#FDFBF7]">
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#8A857D]">{label}</div>
      <div className="mt-1 text-[14px] text-[#2C2A28]">{children}</div>
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">{children}</div>;
}

function KV({ k, v }) {
  return (
    <div className="rounded-sm border border-[#E6E2D8] px-3 py-2.5 bg-white">
      <div className="text-[10px] tracking-[0.18em] uppercase text-[#8A857D] font-semibold">{k}</div>
      <div className="text-[13px] text-[#2C2A28] mt-0.5">{v == null || v === "" ? <span className="text-[#8A857D]">—</span> : String(v)}</div>
    </div>
  );
}
